import React, { FunctionComponent } from 'react'
import { Pressable, View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { BodyText, Size as BodyTextSize } from '../../atoms/bodyText/BodyText'
import { ChevronIcon, ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import { Image } from '../../atoms/image/Image'
import { tID } from '../../utils/utils'

export interface EssentialsBubbleProps {
  title: string
  url: string
  thumbnailURI: string
  articleTypeLabel: string
  readTime: string
  onEssentialsPress: (url: string) => void
}

const Container = styled(Pressable)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  borderRadius: '16px',
  border: `1px solid ${theme.colors.borderDefault}`,
  padding: theme.spacing['12px'],
  backgroundColor: theme.colors.backgroundPrimary,
  marginTop: theme.spacing['12px'],
  cursor: 'pointer',
}))

const ContentContainer = styled.View(({ theme }) => ({
  flex: 1,
  flexShrink: 1,
  marginHorizontal: theme.spacing['8px'],
}))

const Thumbnail = styled(Image)(({ theme }) => ({
  borderRadius: theme.spacing['8px'],
  height: '40px',
  width: '40px',
}))

const Title = styled(BodyText)(({ theme }) => ({
  marginBottom: theme.spacing['4px'],
}))

const MetaContainer = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
})

const Ellipsis = styled.View(({ theme }) => ({
  width: '3px',
  height: '3px',
  borderRadius: '1.5px',
  backgroundColor: theme.colors.textSecondary,
  marginLeft: '6px',
  marginRight: '6px',
}))

export const EssentialsBubble: FunctionComponent<EssentialsBubbleProps> = ({
  title,
  url,
  thumbnailURI,
  articleTypeLabel,
  readTime,
  onEssentialsPress,
}) => {
  const { colors } = useTheme()

  const handlePress = (url: string) => {
    const urlObj = new URL(url)
    const essentialArticle = urlObj.pathname.split('/').pop() || ''
    onEssentialsPress(essentialArticle)
  }

  return (
    <Container
      testID={tID('EssentialsBubble')}
      accessibilityRole='button'
      accessibilityLabel={title}
      onPress={() => handlePress(url)}
      style={({ hovered }) => ({
        backgroundColor: hovered ? colors.backgroundElement : colors.backgroundPrimary,
      })}
    >
      <Thumbnail source={`https://${thumbnailURI}`} />
      <ContentContainer>
        <Title text={title} size={BodyTextSize.SMALL} medium numberOfLines={2} />
        <MetaContainer>
          <BodyText text={articleTypeLabel} size={BodyTextSize.CAPTION} color={colors.textSecondary} medium />
          <Ellipsis />
          <BodyText text={readTime} size={BodyTextSize.CAPTION} color={colors.textSecondary} medium />
        </MetaContainer>
      </ContentContainer>
      <View>
        <ChevronIcon size={16} direction={ChevronIconDirection.RIGHT} fillColor={colors.iconDefault} />
      </View>
    </Container>
  )
}
